import { ComponentProps, Fragment } from 'react';

import { Tooltip } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import PersonIcon from '@material-ui/icons/Person';

import { RiCustomerService2Line, RiSparklingLine } from '@remixicon/react';

import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
  useSidebarPinState,
} from '@backstage/core-components';

import { useFlag } from '@spotify-confidence/react';
import {
  IconDeviceComputer,
  IconExclamationCircle,
  ModuleIcons,
  useAuth,
  useAuthFactory,
  useLocation,
} from '@spotify/confidence-core-react';
import { useAccountMode } from '@spotify/confidence-graphql';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import { useUnthread } from '../../hooks/useUnthread';
import { ProfileMenu } from '../ProfileMenu';
import { getMenuLinks, isMenuItem } from './MenuLinks';

const TooltipSidebarItem = ({
  text = '',
  ...props
}: ComponentProps<typeof SidebarItem>) => {
  const { isPinned } = useSidebarPinState();
  return (
    <Tooltip title={isPinned ? '' : text} placement="right" arrow>
      <SidebarItem text={text} {...props} />
    </Tooltip>
  );
};

const NO_SIDEBAR_PAGES = ['/invitation', '/onboarding', '/signup', '/beta'];

const SupportButton = ({ onClick }: { onClick: () => void }) => {
  const { isPinned } = useSidebarPinState();
  return (
    <Tooltip title={isPinned ? '' : 'Support'} placement="right" arrow>
      <SidebarItem
        text="Support"
        icon={RiCustomerService2Line as any}
        onClick={onClick}
      />
    </Tooltip>
  );
};

export const SidebarMenu = () => {
  const { user, isAuthenticated } = useAuth();
  const { overrideToken } = useAuthFactory();
  const { isTotalConfidence } = useAccountMode();
  const showEvents = useFlag('show-events.enabled', false);
  const showInitiatives = useFlag('initiatives.enabled', false);
  const showCollections = useFlag('collections.enabled', false);
  const showExplore = useFlag('insights-agent.enabled', false);
  const { openChat } = useUnthread();

  const { allowed: canViewInsights } = useCheckResourcePermissions({
    can: 'read',
    name: 'dashboards/global',
  });

  const location = useLocation();
  const pageWithoutSidebar = NO_SIDEBAR_PAGES.some(page =>
    location.pathname.startsWith(page),
  );

  if (!isAuthenticated || pageWithoutSidebar) {
    return null;
  }

  return (
    <Sidebar disableExpandOnHover>
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {getMenuLinks({
          isTotalConfidence,
          showEvents,
          showInitiatives,
          showCollections,
        }).map((m, i) => {
          if (isMenuItem(m)) {
            let targetPath = m.to;
            // Highlight Segments when on materialized segments routes
            const isMaterializedSegmentsActive = location.pathname.startsWith(
              '/materializedSegments',
            );
            if (m.to === '/segments' && isMaterializedSegmentsActive) {
              targetPath = location.pathname;
            }

            return (
              <TooltipSidebarItem
                key={m.to}
                icon={m.icon ?? ModuleIcons.abtests}
                to={targetPath}
                text={m.label}
              />
            );
          }
          return <Fragment key={`menu-item-${i}`}>{m}</Fragment>;
        })}

        {canViewInsights && (
          <>
            <SidebarDivider />
            <TooltipSidebarItem
              icon={ModuleIcons.insights}
              to="/insights"
              text="Insights"
            />
            <SidebarDivider />
          </>
        )}

        {showExplore && (
          <TooltipSidebarItem
            icon={RiSparklingLine as any}
            to="/explore"
            text="Explore"
          />
        )}
      </SidebarGroup>
      <SidebarSpace />
      <SidebarGroup label="Settings" icon={<PersonIcon />}>
        <TooltipSidebarItem
          text="Admin"
          to="/admin"
          icon={IconDeviceComputer}
        />
        {(user?.email?.endsWith('@spotify.com') || overrideToken) && (
          <TooltipSidebarItem
            text="Debug"
            to="/debug"
            icon={IconExclamationCircle}
          />
        )}
        <SupportButton
          onClick={() =>
            openChat(
              user?.name && user?.email
                ? { name: user.name, email: user.email }
                : undefined,
            )
          }
        />
        <SidebarDivider />
        <ProfileMenu />
      </SidebarGroup>
    </Sidebar>
  );
};
